import Phaser from 'phaser';
import { COLORS, GAME } from '../constants';
import { EntityManager, TreeSlot, Tower, Player, TowerType } from '../entities';
import { InputRouter, PlayerId, ALL_PLAYER_IDS } from './InputRouter';
import { RENDER_LAYERS } from './RenderSystem';
import { TowerUnlockSystem } from './TowerUnlockSystem';
import { SessionManager } from './SessionManager';

// ============================================
// Tower Placement System
// ============================================

export interface PlacementConfig {
  towerType: TowerType;
  cost: number;
}

type PlacementState = 'idle' | 'hovering' | 'previewing';

// Player colors for preview tints
const PLAYER_COLORS: Record<PlayerId, number> = {
  'P1': 0x4444ff, // Blue
  'P2': 0xff4444, // Red
  'P3': 0xffff44, // Yellow
  'P4': 0x44ff44, // Green
};

class TowerPlacementSystemManager {
  private scene: Phaser.Scene | null = null;
  private state: PlacementState = 'idle';

  // Per-player tower selection (supports 4 players)
  private playerTowerSelections: Map<PlayerId, TowerType> = new Map(
    ALL_PLAYER_IDS.map(pid => [pid, 'arrow'] as [PlayerId, TowerType])
  );
  // Per-player hovered slot so all players can select/build at the same time
  private hoveredSlotByPlayer: Map<PlayerId, TreeSlot | null> = new Map(
    ALL_PLAYER_IDS.map(pid => [pid, null] as [PlayerId, TreeSlot | null])
  );

  // Per-player graphics for previews
  private ghostTowers: Map<PlayerId, Phaser.GameObjects.Container> = new Map();
  private rangeCircles: Map<PlayerId, Phaser.GameObjects.Arc> = new Map();
  private invalidOverlay: Phaser.GameObjects.Rectangle | null = null;
  private tooltipText: Phaser.GameObjects.Text | null = null;

  // Callbacks
  public onTowerPlaced?: (slot: TreeSlot, tower: Tower, playerId: PlayerId) => void;
  public onPlacementFailed?: (reason: string) => void;
  public onTowerResearched?: (towerType: TowerType) => void;

  // Resource check callback
  private canAfford: (cost: number) => boolean = () => true;
  private spendResources: (cost: number) => void = () => {};

  init(scene: Phaser.Scene): void {
    this.scene = scene;
    this.createPreviewGraphics();
  }

  private createPreviewGraphics(): void {
    if (!this.scene) return;

    // Create per-player ghost towers and range circles
    ALL_PLAYER_IDS.forEach(pid => {
      // Ghost tower container
      const ghost = this.scene!.add.container(0, 0);
      ghost.setVisible(false);
      ghost.setDepth(RENDER_LAYERS.UI_WORLD);
      ghost.setAlpha(0.6);
      this.ghostTowers.set(pid, ghost);

      // Range circle with player color
      const color = PLAYER_COLORS[pid];
      const range = this.scene!.add.circle(0, 0, 100);
      range.setStrokeStyle(3, color, 0.7);
      range.setFillStyle(color, 0.1);
      range.setVisible(false);
      range.setDepth(RENDER_LAYERS.UI_WORLD - 1);
      this.rangeCircles.set(pid, range);

      // Initialize ghost tower content
      this.updateGhostTower(pid);
    });

    // Invalid placement overlay (shared)
    this.invalidOverlay = this.scene.add.rectangle(0, 0, GAME.TOWER_SIZE + 10, GAME.TOWER_SIZE + 10, 0xff0000, 0.3);
    this.invalidOverlay.setVisible(false);
    this.invalidOverlay.setDepth(RENDER_LAYERS.UI_WORLD + 1);

    // Tooltip (shared)
    this.tooltipText = this.scene.add.text(0, 0, '', {
      fontSize: '12px',
      color: '#ffffff',
      backgroundColor: '#000000aa',
      padding: { x: 6, y: 4 },
    });
    this.tooltipText.setOrigin(0.5, 1);
    this.tooltipText.setVisible(false);
    this.tooltipText.setDepth(RENDER_LAYERS.UI_WORLD + 2);
  }

  private updateGhostTower(playerId: PlayerId): void {
    const ghost = this.ghostTowers.get(playerId);
    if (!ghost || !this.scene) return;

    // Clear existing
    ghost.removeAll(true);

    const towerType = this.getSelectedTowerType(playerId);
    const stats = Tower.getStats(towerType);
    const color = PLAYER_COLORS[playerId];

    // Try to use tower icon, fall back to colored rectangle
    const iconKey = `icon-${towerType}`;
    if (this.scene.textures.exists(iconKey)) {
      const img = this.scene.add.image(0, 0, iconKey);
      img.setScale(0.3);
      ghost.add(img);
    } else {
      // Fallback: Tower body rectangle
      const body = this.scene.add.rectangle(0, 0, GAME.TOWER_SIZE, GAME.TOWER_SIZE, stats.color);
      body.setStrokeStyle(2, 0xffffff);

      const label = this.scene.add.text(0, 0, towerType.charAt(0).toUpperCase(), {
        fontSize: '16px',
        color: '#ffffff',
        fontStyle: 'bold',
      });
      label.setOrigin(0.5);

      ghost.add([body, label]);
    }

    // Add player color indicator ring
    const ring = this.scene.add.circle(0, -GAME.TOWER_SIZE / 2 - 8, 6, color);
    ring.setStrokeStyle(1, 0xffffff);
    ghost.add(ring);
  }

  // ============================================
  // Configuration
  // ============================================

  setSelectedTowerType(type: TowerType, playerId?: PlayerId): void {
    const pid = playerId || 'P1';
    this.playerTowerSelections.set(pid, type);
    this.updateGhostTower(pid);
    this.updatePlayerRangeCircle(pid);
  }

  getSelectedTowerType(playerId?: PlayerId): TowerType {
    const pid = playerId || 'P1';
    return this.playerTowerSelections.get(pid) || 'arrow';
  }

  getAllPlayerSelections(): Map<PlayerId, TowerType> {
    return this.playerTowerSelections;
  }

  cycleTowerType(direction: number, playerId: PlayerId, availableTypes?: TowerType[]): void {
    const types = availableTypes || Tower.getAllTypes();
    const currentType = this.getSelectedTowerType(playerId);
    const currentIndex = types.indexOf(currentType);
    if (currentIndex === -1 && types.length > 0) {
      this.setSelectedTowerType(types[0], playerId);
      return;
    }
    const newIndex = (currentIndex + direction + types.length) % types.length;
    this.setSelectedTowerType(types[newIndex], playerId);
  }

  setResourceCallbacks(
    canAfford: (cost: number) => boolean,
    spend: (cost: number) => void
  ): void {
    this.canAfford = canAfford;
    this.spendResources = spend;
  }

  // ============================================
  // Update Loop
  // ============================================

  update(): void {
    if (!this.scene) return;

    // Update hover based on pointer or player position
    this.updateHover();

    // Handle input
    this.handleInput();

    // Update preview visuals
    this.updatePreviewVisuals();
  }

  /**
   * Call once at the start of the frame (before INTERACT handling) so that
   * hoveredSlotByPlayer is current when the player presses INTERACT or clicks.
   * Fixes needing to click twice when building via mouse (INTERACT ran with stale hover).
   */
  updateHoverState(): void {
    if (!this.scene) return;
    this.updateHover();
  }

  private updateHover(): void {
    const slots = EntityManager.getByType<TreeSlot>('treeSlot');
    const players = EntityManager.getByType<Player>('player');
    const pointerPos = InputRouter.getPointerWorldPosition();
    const hoverRadius = GAME.TREE_SLOT_RADIUS * 2;
    const playerHoverRadius = 80;

    // Update hover for each player
    ALL_PLAYER_IDS.forEach(pid => {
      let closest: TreeSlot | null = null;
      let closestDist = playerHoverRadius;

      // P1 also checks pointer (mouse) position
      if (pid === 'P1') {
        closestDist = hoverRadius;
        for (const slot of slots) {
          if (slot.isOccupied()) continue;
          const dist = Phaser.Math.Distance.Between(pointerPos.x, pointerPos.y, slot.x, slot.y);
          if (dist < closestDist) {
            closestDist = dist;
            closest = slot;
          }
        }
      }

      // Check player character position
      const player = players.find(p => p.playerId === pid);
      if (player) {
        for (const slot of slots) {
          if (slot.isOccupied()) continue;
          const dist = player.distanceTo(slot);
          if (dist < playerHoverRadius && dist < closestDist) {
            closestDist = dist;
            closest = slot;
          }
        }
      }

      this.setPlayerHoveredSlot(pid, closest);
    });

    const anyHover = ALL_PLAYER_IDS.some(pid => this.hoveredSlotByPlayer.get(pid));
    this.state = anyHover ? 'hovering' : 'idle';
  }

  private setPlayerHoveredSlot(playerId: PlayerId, slot: TreeSlot | null): void {
    const prev = this.hoveredSlotByPlayer.get(playerId) ?? null;
    if (prev === slot) return;
    prev?.showHighlight(false, playerId);
    slot?.showHighlight(true, playerId);
    this.hoveredSlotByPlayer.set(playerId, slot);
  }

  private handleInput(): void {
    // INTERACT (build/upgrade) is handled in GameplayScene so we can prefer upgrade when on a built tower

    // Pointer click placement (P1)
    if (InputRouter.wasPointerJustDown()) {
      this.tryPlaceTower('P1');
    }

    // Cancel per player
    ALL_PLAYER_IDS.forEach(pid => {
      if (InputRouter.wasActionJustPressed(pid, 'CANCEL')) {
        this.cancelPlacement(pid);
      }
    });
  }

  private updatePreviewVisuals(): void {
    // Show per-player previews at each player's hovered slot
    let anyHovered = false;

    ALL_PLAYER_IDS.forEach(pid => {
      const slot = this.hoveredSlotByPlayer.get(pid) ?? null;
      const ghost = this.ghostTowers.get(pid);
      const range = this.rangeCircles.get(pid);

      if (!slot) {
        ghost?.setVisible(false);
        range?.setVisible(false);
        return;
      }

      anyHovered = true;
      const towerType = this.getSelectedTowerType(pid);
      const stats = Tower.getStats(towerType);
      const cost = Tower.getCost(towerType);
      const canAfford = this.canAfford(cost);
      const needsResearch = TowerUnlockSystem.needsResearch(towerType);
      const gemCost = needsResearch ? TowerUnlockSystem.getResearchCost(towerType) : 0;
      const canAffordGems = !needsResearch || SessionManager.canAffordGems(gemCost);
      const isValid = !slot.isOccupied() && canAfford && canAffordGems;
      const color = PLAYER_COLORS[pid];

      // Position ghost tower
      if (ghost) {
        ghost.setPosition(slot.x, slot.y);
        ghost.setVisible(true);
        ghost.setAlpha(isValid ? 0.7 : 0.4);
      }

      // Position and update range circle with player color
      if (range) {
        range.setPosition(slot.x, slot.y);
        range.setRadius(stats.range);
        range.setVisible(true);
        range.setStrokeStyle(3, isValid ? color : 0xff0000, 0.7);
        range.setFillStyle(isValid ? color : 0xff0000, 0.1);
      }
    });

    // Hide shared tooltip/overlay if no hover
    if (!anyHovered) {
      this.invalidOverlay?.setVisible(false);
      this.tooltipText?.setVisible(false);
    }
  }

  private updatePlayerRangeCircle(playerId: PlayerId): void {
    const range = this.rangeCircles.get(playerId);
    if (range) {
      const towerType = this.getSelectedTowerType(playerId);
      const stats = Tower.getStats(towerType);
      range.setRadius(stats.range);
    }
  }

  private hidePreview(): void {
    this.ghostTowers.forEach(g => g?.setVisible(false));
    this.rangeCircles.forEach(r => r?.setVisible(false));
    this.invalidOverlay?.setVisible(false);
    this.tooltipText?.setVisible(false);
  }

  // ============================================
  // Placement Logic
  // ============================================

  tryPlaceTower(playerId: PlayerId): boolean {
    const slot = this.hoveredSlotByPlayer.get(playerId) ?? null;
    if (!slot || !this.scene) {
      return false;
    }

    const towerType = this.getSelectedTowerType(playerId);

    // Check if slot is occupied
    if (slot.isOccupied()) {
      this.onPlacementFailed?.('Slot is occupied');
      this.showPlacementError('Slot occupied!', slot);
      return false;
    }

    // Costs: first-time = gems (unlock) + coins (build); after unlock = coins only
    const coinCost = Tower.getCost(towerType);
    const needsResearch = TowerUnlockSystem.needsResearch(towerType);
    const gemCost = needsResearch ? TowerUnlockSystem.getResearchCost(towerType) : 0;

    if (needsResearch) {
      // First time only: require gems (unlock) + coins (build). researchTower() spends gems; we spend coins.
      if (!SessionManager.canAffordGems(gemCost)) {
        this.onPlacementFailed?.('Not enough gems');
        this.showPlacementError(`Need ${gemCost} gems to unlock!`, slot);
        return false;
      }
      if (!this.canAfford(coinCost)) {
        this.onPlacementFailed?.('Not enough coins');
        this.showPlacementError('Not enough coins!', slot);
        return false;
      }
      TowerUnlockSystem.researchTower(towerType); // spends gems once, marks researched (never again this session)
      this.spendResources(coinCost);
      console.log(`[TowerPlacement] ${playerId} unlocked ${towerType} for ${gemCost} gems + ${coinCost} coins`);
      this.onTowerResearched?.(towerType);
    } else {
      // Already unlocked: only charge coins
      if (!this.canAfford(coinCost)) {
        this.onPlacementFailed?.('Not enough coins');
        this.showPlacementError('Not enough coins!', slot);
        return false;
      }
      this.spendResources(coinCost);
    }

    // Create tower
    const tower = new Tower(this.scene, {
      id: `tower-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
      x: slot.x,
      y: slot.y,
      towerType: towerType,
    });

    EntityManager.add(tower);
    slot.setOccupied(tower.id);

    // Callback
    this.onTowerPlaced?.(slot, tower, playerId);

    // Visual feedback
    this.showPlacementSuccess(slot);

    console.log(`[TowerPlacement] ${playerId} placed ${towerType} tower at (${slot.x}, ${slot.y})`);

    return true;
  }

  private cancelPlacement(playerId: PlayerId): void {
    const slot = this.hoveredSlotByPlayer.get(playerId) ?? null;
    slot?.showHighlight(false, playerId);
    this.hoveredSlotByPlayer.set(playerId, null);
    const anyHover = ALL_PLAYER_IDS.some(pid => this.hoveredSlotByPlayer.get(pid));
    if (!anyHover) {
      this.state = 'idle';
      this.hidePreview();
    }
  }

  // ============================================
  // Visual Feedback
  // ============================================

  private showPlacementSuccess(slot: TreeSlot): void {
    if (!this.scene) return;

    // Brief flash
    const flash = this.scene.add.circle(slot.x, slot.y, GAME.TOWER_SIZE, 0x00ff00, 0.5);
    flash.setDepth(RENDER_LAYERS.FX);

    this.scene.tweens.add({
      targets: flash,
      alpha: 0,
      scale: 1.5,
      duration: 200,
      onComplete: () => flash.destroy(),
    });
  }

  private showPlacementError(message: string, slot: TreeSlot): void {
    if (!this.scene) return;

    // Show error text
    const errorText = this.scene.add.text(slot.x, slot.y - 40, message, {
      fontSize: '14px',
      color: '#ff4444',
      fontStyle: 'bold',
    });
    errorText.setOrigin(0.5);
    errorText.setDepth(RENDER_LAYERS.FX);

    this.scene.tweens.add({
      targets: errorText,
      y: errorText.y - 30,
      alpha: 0,
      duration: 800,
      onComplete: () => errorText.destroy(),
    });

    // Shake the invalid overlay at this slot
    if (this.invalidOverlay) {
      this.invalidOverlay.setPosition(slot.x, slot.y);
      this.invalidOverlay.setVisible(true);
      this.scene.tweens.add({
        targets: this.invalidOverlay,
        x: this.invalidOverlay.x + 5,
        yoyo: true,
        repeat: 3,
        duration: 50,
      });
    }
  }

  // ============================================
  // Utility
  // ============================================

  getClosestSlot(worldX: number, worldY: number, maxRange: number = 100): TreeSlot | null {
    const slots = EntityManager.getByType<TreeSlot>('treeSlot');
    let closest: TreeSlot | null = null;
    let closestDist = maxRange;

    for (const slot of slots) {
      if (slot.isOccupied()) continue;
      const dist = Phaser.Math.Distance.Between(worldX, worldY, slot.x, slot.y);
      if (dist < closestDist) {
        closestDist = dist;
        closest = slot;
      }
    }

    return closest;
  }

  getHoveredSlot(playerId?: PlayerId): TreeSlot | null {
    if (playerId) return this.hoveredSlotByPlayer.get(playerId) ?? null;
    return this.hoveredSlotByPlayer.get('P1') ?? this.hoveredSlotByPlayer.get('P2') ?? null;
  }

  getCost(type: TowerType): number {
    return Tower.getCost(type);
  }

  destroy(): void {
    this.ghostTowers.forEach(g => g?.destroy());
    this.ghostTowers.clear();
    this.rangeCircles.forEach(r => r?.destroy());
    this.rangeCircles.clear();
    this.invalidOverlay?.destroy();
    this.tooltipText?.destroy();
    this.invalidOverlay = null;
    this.tooltipText = null;
    ALL_PLAYER_IDS.forEach(pid => this.hoveredSlotByPlayer.set(pid, null));
    this.scene = null;
  }
}

// Singleton instance
export const TowerPlacementSystem = new TowerPlacementSystemManager();
